import { useCallback } from "react";

import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import type { FilterFieldType } from "#app/lib/filters/types";

/**
 * Whether a filter type has anything to pick — a filter over an empty pool is
 * a dead end (expand "Team" in a workspace with no teams and there is nothing
 * to check), so the panel hides those sections/fields entirely, the same
 * honest-affordance rule as the tag manager's drag grip.
 *
 * Two deliberate holes:
 * - Everything reports available until workspace data hydrates — pools start
 *   `[]` while the query is in flight, and hiding on that would flicker
 *   sections out and back on first open.
 * - `issue-type` is always available: the default "Project" type is seeded at
 *   workspace birth, so an empty list only ever means the (separate) types
 *   query hasn't landed yet.
 *
 * Call sites add their own second guard: a section/field whose value is
 * APPLIED stays visible even over an empty pool, so a filter never becomes
 * uninspectable (set a team filter, teams get deleted — the row must remain
 * to show and clear it).
 */
export function useFilterTypeAvailability(): (type: FilterFieldType) => boolean {
  const { users, teams, workflows, stages, isHydrated } = useWorkspaceData();

  return useCallback(
    (type: FilterFieldType) => {
      if (!isHydrated) return true;
      switch (type) {
        case "entity-user":
        case "entity-assignee":
          return users.length > 0;
        case "entity-team":
          return teams.length > 0;
        case "workflow":
        case "workflow-stage":
          return workflows.length > 0;
        case "stage":
          return stages.length > 0;
        // A custom-field def only exists in the config when the workspace
        // field exists, so reaching here means there is something to filter.
        case "custom-field":
          return true;
        // Static enums, free inputs, and birth-seeded pools (issue-type).
        default:
          return true;
      }
    },
    [isHydrated, users, teams, workflows, stages],
  );
}
